import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, PhoneOff, Mic, Video, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import Avatar from '../components/Avatar';
import InsufficientBalanceDialog from '../components/InsufficientBalanceDialog';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { langFlag } from '../lib/creatorMeta';
import { getAvailableCreators, initiateCall } from '../api/o2oCalls';

const StateDot = ({ state }) => (
  <span
    className={`h-2.5 w-2.5 rounded-full ${state === 'available' ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}
    aria-hidden="true"
  />
);

const CreatorCard = ({ c, onCall, busyId, t }) => {
  const available = c.state === 'available';
  const isBusyBtn = busyId === c.creator_id;
  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-border bg-card p-3.5 transition-colors hover:border-amber-500/40"
      data-testid={`calls-creator-card-${c.username}`}
    >
      <Link to={`/u/${c.username}`} className="shrink-0">
        <Avatar src={c.avatar_url} alt={c.display_name || c.username} size={52} />
      </Link>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <StateDot state={c.state} />
          <Link to={`/u/${c.username}`} className="truncate text-sm font-semibold hover:underline">
            {c.display_name || c.username}
          </Link>
          {c.languages?.length > 0 && (
            <span className="text-xs">{c.languages.map(langFlag).join(' ')}</span>
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">@{c.username}</p>
        <p className="mt-0.5 text-xs font-semibold gold-text" data-testid={`calls-creator-rate-${c.username}`}>
          {t('directory.rate_per_min', { rate: c.rate_credits_per_min })}
        </p>
      </div>
      {available ? (
        <Button
          onClick={() => onCall(c)}
          disabled={isBusyBtn || c.is_me}
          className="h-9 shrink-0 rounded-full bg-emerald-600 px-4 text-sm font-semibold text-white hover:bg-emerald-700"
          data-testid={`calls-call-btn-${c.username}`}
        >
          {isBusyBtn
            ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            : <Phone className="mr-1.5 h-4 w-4" />}
          {t('directory.call_btn')}
        </Button>
      ) : (
        <Button
          disabled
          className="h-9 shrink-0 rounded-full bg-red-600 px-4 text-sm font-semibold text-white opacity-90 disabled:opacity-90"
          data-testid={`calls-busy-btn-${c.username}`}
        >
          <PhoneOff className="mr-1.5 h-4 w-4" />
          {t('directory.busy_btn')}
        </Button>
      )}
    </div>
  );
};

export default function CallsAvailabilityPage() {
  const { t } = useTranslation('o2o');
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modeTarget, setModeTarget] = useState(null);
  const [callingId, setCallingId] = useState(null);
  const [insufficient, setInsufficient] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    try {
      const res = await getAvailableCreators();
      setData(res);
    } catch (_e) { /* silent */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCall = async (creator, mode) => {
    setModeTarget(null);
    setCallingId(creator.creator_id);
    try {
      const call = await initiateCall(creator.creator_id, mode);
      navigate(`/call/${call.id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        setInsufficient({ required: data?.min_credits || 0, balance: me?.wallet_balance || 0 });
      } else {
        const detail = e.response?.data?.detail || '';
        toast({
          title: t('directory.call_failed'),
          description: String(detail).split('|').pop() || t('directory.call_failed_desc'),
          variant: 'destructive',
        });
        load();
      }
    } finally {
      setCallingId(null);
    }
  };

  const onCall = (creator) => {
    if (creator.audio_enabled && creator.video_enabled) setModeTarget(creator);
    else startCall(creator, creator.video_enabled ? 'video' : 'audio');
  };

  const creators = data?.creators || [];

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 pb-24 pt-4 md:pb-8" data-testid="calls-availability-page">
      <header className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-400 gold-text">
          <Phone className="h-6 w-6" strokeWidth={1.6} />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('directory.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('directory.subtitle')}</p>
        </div>
      </header>

      {loading && (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground" data-testid="calls-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('directory.loading')}
        </div>
      )}

      {!loading && creators.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border py-16 text-center" data-testid="calls-empty-state">
          <PhoneOff className="h-10 w-10 text-muted-foreground/50" strokeWidth={1.4} />
          <p className="text-sm font-medium text-muted-foreground">{t('directory.empty')}</p>
        </div>
      )}

      {!loading && creators.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="calls-creator-list">
          {creators.map((c) => (
            <CreatorCard key={c.creator_id} c={c} onCall={onCall} busyId={callingId} t={t} />
          ))}
        </div>
      )}

      {/* Audio/Video mode picker */}
      <Dialog open={!!modeTarget} onOpenChange={(v) => !v && setModeTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="calls-mode-dialog">
          <DialogHeader>
            <DialogTitle>{t('directory.mode_dialog_title', { username: modeTarget?.username })}</DialogTitle>
            <DialogDescription>
              {t('directory.mode_dialog_desc', {
                rate: modeTarget?.rate_credits_per_min,
                max: data?.max_duration_minutes,
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Button onClick={() => startCall(modeTarget, 'audio')} variant="secondary" className="h-16 flex-col gap-1" data-testid="calls-mode-audio">
              <Mic className="h-5 w-5" /> {t('directory.mode_audio')}
            </Button>
            <Button onClick={() => startCall(modeTarget, 'video')} className="h-16 flex-col gap-1 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="calls-mode-video">
              <Video className="h-5 w-5" /> {t('directory.mode_video')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <InsufficientBalanceDialog
        open={!!insufficient}
        onOpenChange={(v) => !v && setInsufficient(null)}
        balance={insufficient?.balance || 0}
        required={insufficient?.required || 0}
        context="call"
      />
    </div>
  );
}
